/**
 * worker-index-allocator.ts
 *
 * Monotonic Worker Index Allocation with Controlled Generation Compaction.
 * Invariant: instanceIndex strictly increments during dynamic scaling (i0, i1, i2...),
 * and generation only bumps during a full pool reconciliation.
 */

import { WorkerIndexAllocator } from '../contracts/model-orchestration-contracts.js';

export class MonotonicWorkerIndexAllocator {
  private readonly allocators = new Map<string, WorkerIndexAllocator>(); // `${nodeId}:${modelId}` -> Allocator

  private getKey(nodeId: string, modelId: string): string {
    return `${nodeId}:${modelId}`;
  }

  getOrCreateAllocator(nodeId: string, modelId: string): WorkerIndexAllocator {
    const key = this.getKey(nodeId, modelId);
    let alloc = this.allocators.get(key);
    if (!alloc) {
      alloc = {
        nodeId,
        modelId,
        nextIndex: 0,
        generation: 0,
      };
      this.allocators.set(key, alloc);
    }
    return alloc;
  }

  /**
   * Allocates the next monotonic worker index without reusing past indices.
   */
  allocateNext(nodeId: string, modelId: string): { instanceIndex: number; generation: number } {
    const key = this.getKey(nodeId, modelId);
    const alloc = this.getOrCreateAllocator(nodeId, modelId);

    const assignedIndex = alloc.nextIndex;
    const updated: WorkerIndexAllocator = {
      ...alloc,
      nextIndex: assignedIndex + 1,
    };
    this.allocators.set(key, updated);

    return {
      instanceIndex: assignedIndex,
      generation: alloc.generation,
    };
  }

  /**
   * Bumps the generation counter and resets index to 0 during controlled pool reconciliation.
   */
  reconcileAndBumpGeneration(nodeId: string, modelId: string): { generation: number } {
    const key = this.getKey(nodeId, modelId);
    const alloc = this.getOrCreateAllocator(nodeId, modelId);

    const nextGeneration = alloc.generation + 1;
    const updated: WorkerIndexAllocator = {
      nodeId,
      modelId,
      nextIndex: 0,
      generation: nextGeneration,
    };
    this.allocators.set(key, updated);

    return { generation: nextGeneration };
  }

  clear(): void {
    this.allocators.clear();
  }
}
